/**
 * SubstitutesCard — replica subs_comps.jsx:130-162 SubsList default.
 *
 * UI-04: Layout list default (más legible single-handed). Tag "más económico"
 * cheap green al item de menor `precio` (si todos null, omite tag — backend
 * v1 NO emite precio aún hasta Phase 5).
 *
 * 2026-05-08 — Multi-PA awareness: cuando los subs tienen `principio_activo`
 * distinto al `generic` del primary (caso FLOW-01 con multi-opción
 * farmacológica), cambiamos el título a "Otras opciones" y renderizamos el
 * `tag` (rationale del par síntoma+PA) como pill colored al lado del nombre.
 * Cuando son misma marca (FLOW-02/03 substitutes), seguimos mostrando
 * "Otras marcas con X" como antes.
 */
import { textoPa } from '../utils/textoPa'
import type { AlternativaItem } from '../types/product_card'
import { Icon } from './icons'

interface Props {
  subs: AlternativaItem[]
  generic: string
  onPick: (s: AlternativaItem) => void
}

interface AltWithPrice extends AlternativaItem {
  precio?: number | null
}

// 2026-10-10 (pedido de Hipermaxi): etiquetas comerciales que se ven de un vistazo
const ETIQUETAS: { prefijo: string; fondo: string; texto: string }[] = [
  { prefijo: 'Sustituto', fondo: 'var(--color-info-soft, #dbeafe)', texto: 'var(--color-info, #1d4ed8)' },
  { prefijo: 'Más barato', fondo: 'var(--color-ok-soft, #d1fae5)', texto: 'var(--color-ok, #047857)' },
  { prefijo: 'Mejor margen', fondo: 'var(--color-brand-soft)', texto: 'var(--color-brand-ink)' },
  { prefijo: 'Promoción', fondo: 'var(--color-warn-soft, #fef3c7)', texto: '#92400e' },
]

export function etiquetasComerciales(tag?: string | null): { texto: string; fondo: string; color: string }[] {
  if (!tag) return []
  return tag.split(' · ').flatMap((t) => {
    const e = ETIQUETAS.find((x) => t.startsWith(x.prefijo))
    return e ? [{ texto: t, fondo: e.fondo, color: e.texto }] : []
  })
}

function bsCorto(n: number) {
  return `Bs ${n.toFixed(2).replace('.', ',')}`
}

function nameOf(s: AlternativaItem): string {
  return s.nombre_comercial ?? s.nombre ?? '—'
}

export function SubstitutesCard({ subs, generic, onPick }: Props) {
  if (subs.length === 0) return null

  // Cheapest detect — backend v1 NO emite precio en alternativas. Cuando lo emita,
  // este accessor lo recoge sin breaking change.
  const withPrice = subs as AltWithPrice[]
  const cheapestSku = (() => {
    const priced = withPrice.filter(
      (s) => typeof s.precio === 'number' && Number.isFinite(s.precio),
    )
    if (priced.length === 0) return null
    return priced.reduce((m, s) => ((s.precio as number) < (m.precio as number) ? s : m)).sku_id
  })()

  // Detectar diversidad de PAs entre los subs vs el generic del primary.
  // Si TODOS los subs comparten el mismo PA que el primary → "Otras marcas
  // con X" (FLOW-02/03 substitutes). Si hay PAs distintos → "Otras opciones"
  // (FLOW-01 multi-PA del síntoma).
  const genericLower = generic.toLowerCase().trim()
  const hasMixedPas = subs.some(
    (s) =>
      s.principio_activo &&
      s.principio_activo.toLowerCase().trim() !== genericLower,
  )
  const conEtiquetas = subs.some((s) => etiquetasComerciales(s.tag).length > 0)
  const headerText = conEtiquetas || hasMixedPas ? 'Sustitutos' : `Otras marcas con ${generic}`
  const countLabel = hasMixedPas
    ? `${subs.length} ${subs.length === 1 ? 'opción' : 'opciones'}`
    : `${subs.length} ${subs.length === 1 ? 'marca' : 'marcas'}`

  return (
    <div className="subs-card">
      <div className="flex items-center gap-2">
        <span style={{ color: 'var(--color-brand-ink)' }}>
          <Icon.Shuffle />
        </span>
        <div
          className="text-[14px] font-semibold flex-1"
          style={{ color: 'var(--color-text)' }}
        >
          {headerText}
        </div>
        <span
          className="text-[12px] font-mono uppercase tracking-wider"
          style={{
            padding: '3px 8px',
            borderRadius: 999,
            background: 'var(--color-brand-soft)',
            color: 'var(--color-brand-ink)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          {countLabel}
        </span>
      </div>

      <div className="subs-list">
        {subs.map((s) => {
          const comerciales = etiquetasComerciales(s.tag)
          // 12-oct: bajo "Sustitutos" un paracetamol junto al jarabe de hiedra no es equivalente
          const otroPa = !!s.principio_activo && s.principio_activo.toLowerCase().trim() !== genericLower
          const pills = otroPa && comerciales.length === 0
            ? [{ texto: 'Otro principio activo', fondo: 'var(--color-warn-soft, #fef3c7)', color: '#92400e' }]
            : comerciales
          const isCheap = !conEtiquetas && cheapestSku !== null && s.sku_id === cheapestSku
          // Mostrar tag rationale solo si NO es el sentinel 'cheap' interno ni una etiqueta comercial.
          const showRationaleTag =
            s.tag && s.tag !== 'cheap' && s.tag !== 'más económico' && s.tag !== 'Otra opción' && comerciales.length === 0
          return (
            <button
              key={s.sku_id}
              type="button"
              onClick={() => onPick(s)}
              className="sub-item text-left"
              style={{ minHeight: 44 }}
            >
              <div className="grid gap-1 min-w-0">
                <div
                  className="sub-brand text-[15px] font-medium"
                  style={{ color: 'var(--color-text)' }}
                >
                  {nameOf(s)}
                  {pills.map((p) => (
                    <span
                      key={p.texto}
                      className="text-[13px] font-semibold"
                      style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 999, background: p.fondo, color: p.color,
                               whiteSpace: 'nowrap' }}
                    >
                      {p.texto}
                    </span>
                  ))}
                  {isCheap && (
                    <span className="tag cheap" style={{ marginLeft: 6 }}>
                      más económico
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap text-[13px]">
                  <span
                    style={{
                      padding: '2px 7px',
                      borderRadius: 999,
                      background: 'var(--color-brand-soft)',
                      color: 'var(--color-brand-ink)',
                      fontFamily: 'var(--font-mono)',
                      fontWeight: 600,
                    }}
                  >
                    SKU {s.sku_id}
                  </span>
                  {s.principio_activo && (
                    <span style={{ color: 'var(--color-text-muted)' }}>
                      {textoPa(s.principio_activo)}
                    </span>
                  )}
                  {s.laboratorio && (
                    <span style={{ color: 'var(--color-text-muted)' }}>
                      Lab {s.laboratorio}
                    </span>
                  )}
                  {s.stock_sucursal !== undefined && s.stock_sucursal !== null && (
                    <span
                      style={{
                        padding: '1px 6px',
                        borderRadius: 999,
                        background: s.stock_sucursal > 0 ? 'var(--color-ok-soft, #d1fae5)' : 'var(--color-warn-soft, #fee2e2)',
                        color: s.stock_sucursal > 0 ? 'var(--color-ok, #047857)' : 'var(--color-warn, #b91c1c)',
                        border: `1px solid ${s.stock_sucursal > 0 ? 'var(--color-ok, #10b981)' : 'var(--color-warn, #dc2626)'}`,
                        fontWeight: 600,
                      }}
                    >
                      {s.stock_sucursal > 0 ? `${s.stock_sucursal} u. acá` : 'sin stock acá'}
                    </span>
                  )}
                  {typeof s.precio_sucursal === 'number' && s.precio_sucursal > 0 && (
                    <span className="tabular-nums" style={{ color: 'var(--color-text)', fontWeight: 600 }}>
                      {bsCorto(s.precio_sucursal)}
                    </span>
                  )}
                  {(s.stock_sucursal === undefined || s.stock_sucursal === null) &&
                    s.stock_total !== undefined && s.stock_total !== null && s.stock_total <= 0 && (
                    <span
                      style={{
                        padding: '1px 6px',
                        borderRadius: 999,
                        background: 'var(--color-warn-soft, #fee2e2)',
                        color: 'var(--color-warn, #b91c1c)',
                        border: '1px solid var(--color-warn, #dc2626)',
                        fontWeight: 600,
                      }}
                    >
                      <Icon.Alert width={13} height={13} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 3 }} />agotado
                    </span>
                  )}
                  {s.requiere_receta === true && (
                    <span
                      style={{
                        padding: '1px 6px',
                        borderRadius: 999,
                        background: '#fef3c7',
                        color: '#92400e',
                        border: '1px solid #f59e0b',
                        fontWeight: 600,
                      }}
                    >
                      <Icon.Rx width={13} height={13} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 3 }} />con receta
                    </span>
                  )}
                </div>
                {showRationaleTag && (
                  <div
                    className="text-[13px] italic"
                    style={{ color: 'var(--color-brand-ink)' }}
                  >
                    → {s.tag}
                  </div>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
